import { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Terminal, Search, Package, Server, Plus, Filter, X, Tag } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { LoginArea } from '@/components/auth/LoginArea';
import { SkillBrowser } from '@/components/SkillBrowser';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { SkillType } from '@/lib/skills';

export default function Browse() {
  useSeoMeta({
    title: 'Browse Skills - SkillForge',
    description: 'Discover AI agent skills and MCP servers published by the community.',
  });

  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [type, setType] = useState<SkillType | 'all'>('all');

  const activeTag = searchParams.get('tag') || undefined;

  // Sync search input with URL param on mount
  useEffect(() => {
    const tagParam = searchParams.get('tag');
    if (tagParam) setSearch('');
  }, [searchParams]);

  function clearTag() {
    navigate('/browse');
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
              <Terminal className="w-4 h-4 text-orange-400" />
            </div>
            <span className="font-semibold text-lg tracking-tight">SkillForge</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/browse" className="text-foreground font-medium">Browse</Link>
            <Link to="/connect-agent" className="hover:text-foreground transition-colors">Connect Agent</Link>
            <Link to="/publish" className="hover:text-foreground transition-colors opacity-50 hover:opacity-100 text-xs">Publish</Link>
          </nav>

          <div className="flex items-center gap-3">
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-10">
        {/* Page Header */}
        <div className="mb-10">
          <h1 className="text-4xl font-bold mb-3">Browse Skills</h1>
          <p className="text-muted-foreground text-lg">
            Discover AI agent skills and MCP servers published by developers worldwide.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search skills..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50 focus:ring-orange-500/20"
            />
          </div>

          <Tabs value={type} onValueChange={(v) => setType(v as SkillType | 'all')}>
            <TabsList className="bg-white/[0.03] border border-white/[0.08]">
              <TabsTrigger value="all" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/80 data-[state=active]:to-amber-500/80">
                <Filter className="w-4 h-4" />
                All
              </TabsTrigger>
              <TabsTrigger value="skill" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/80 data-[state=active]:to-amber-500/80">
                <Package className="w-4 h-4" />
                Skills
              </TabsTrigger>
              <TabsTrigger value="mcp" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/80 data-[state=active]:to-amber-500/80">
                <Server className="w-4 h-4" />
                MCPs
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Active tag filter pill */}
        {activeTag && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-muted-foreground">Filtered by tag:</span>
            <Badge
              className="gap-1.5 pl-2 pr-1.5 py-1 bg-orange-500/20 border border-orange-500/40 text-orange-400 hover:bg-orange-500/30 transition-colors cursor-default"
            >
              <Tag className="w-3 h-3" />
              {activeTag}
              <button
                onClick={clearTag}
                className="ml-0.5 rounded-full hover:bg-orange-500/30 p-0.5 transition-colors"
                aria-label="Clear tag filter"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          </div>
        )}

        {/* Skills Grid */}
        <SkillBrowser
          type={type === 'all' ? undefined : type}
          tag={activeTag}
          search={search}
          limit={50}
        />
      </main>
    </div>
  );
}
